/**
 * ⌘K.
 *
 * Every destination and every asset by code, from anywhere, without the mouse.
 * The predecessor had a search box; this has the thing an operator reaches for
 * when an alert names CK1242 and they want it on screen now.
 *
 * `cmdk` does the filtering and the list keyboarding; Radix Dialog underneath
 * it does focus and dismissal. Items are passed in rather than fetched, because
 * the shell already holds the fleet and a second fetch could disagree with it.
 */

import { Command } from 'cmdk';
import { Search } from 'lucide-react';
import { useEffect } from 'react';

export interface PaletteItem {
  id: string;
  /** What is searched and shown. */
  label: string;
  /** Searched but shown smaller: a site name, a kind. */
  hint?: string;
  group: string;
  icon?: React.ReactNode;
  /** The keys that do this without the palette, shown on the right: `g f`. */
  shortcut?: string;
  onSelect: () => void;
}

export function CommandPalette({
  open,
  onOpenChange,
  items,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  items: readonly PaletteItem[];
}): React.JSX.Element {
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        onOpenChange(!open);
      }
    };
    document.addEventListener('keydown', onKey);
    return (): void => document.removeEventListener('keydown', onKey);
  }, [open, onOpenChange]);

  const groups = new Map<string, PaletteItem[]>();
  for (const item of items) groups.set(item.group, [...(groups.get(item.group) ?? []), item]);

  return (
    <Command.Dialog
      open={open}
      onOpenChange={onOpenChange}
      label="Go to"
      overlayClassName="drawer-scrim"
      contentClassName="palette-content"
    >
      <div className="palette-input">
        <Search size={16} className="muted" />
        <Command.Input placeholder="Go to a screen, or type an asset code…" autoFocus />
        <kbd>Esc</kbd>
      </div>
      <Command.List className="palette-list">
        <Command.Empty className="palette-empty">Nothing matches.</Command.Empty>
        {[...groups.entries()].map(([group, entries]) => (
          <Command.Group key={group} heading={group} className="palette-group">
            {entries.map((item) => (
              <Command.Item
                key={item.id}
                // The id keeps two items with the same words distinct: cmdk keys on `value`.
                value={`${item.label} ${item.hint ?? ''} ${item.id}`}
                onSelect={() => {
                  onOpenChange(false);
                  item.onSelect();
                }}
                className="palette-item"
              >
                {item.icon !== undefined && <span className="palette-icon">{item.icon}</span>}
                <span>{item.label}</span>
                {item.hint !== undefined && <span className="palette-hint">{item.hint}</span>}
                {item.shortcut !== undefined && <kbd className="palette-kbd">{item.shortcut}</kbd>}
              </Command.Item>
            ))}
          </Command.Group>
        ))}
      </Command.List>
    </Command.Dialog>
  );
}
